import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { buildCustomerData } from './kunddata';

// Fälten som offertsnurran (Flatbox-appen) tar som input. Ordningen speglar
// formuläret där, så att fält-för-fält-kopiering går uppifrån och ner.
const FIELDS = [
  { key: 'kund',        label: 'Kund',         textLabel: 'Kund' },
  { key: 'kundOrgNr',   label: 'Kund org.nr',  textLabel: 'Org.nr' },
  { key: 'kundAdress',  label: 'Kund adress',  textLabel: 'Adress' },
  { key: 'kundKontakt', label: 'Kundkontakt',  textLabel: 'Kontakt' },
  { key: 'kundEpost',   label: 'Kund e-post',  textLabel: 'E-post' },
  { key: 'kundTelefon', label: 'Kund telefon', textLabel: 'Telefon' },
];

// buildCustomerData ligger i kunddata.js så att konfiguratorn kan använda den
// utan MUI; exporteras vidare härifrån.
export { buildCustomerData };

export default function CopyCustomerDataDialog({ open, onClose, company, contacts = [], defaultContactId = '' }) {
  const [contactId, setContactId] = useState('');
  const [copied, setCopied] = useState('');

  // Förvald kontakt: leadets huvudkontakt, annars kundens primära, annars första.
  useEffect(() => {
    if (!open) return;
    const primary = contacts.find(c => c.id === defaultContactId)
      || contacts.find(c => c.primary) || contacts[0];
    setContactId(primary?.id || '');
    setCopied('');
  }, [open, contacts, defaultContactId]);

  const contact = useMemo(
    () => contacts.find(c => c.id === contactId) || null,
    [contacts, contactId]
  );
  const data = useMemo(() => buildCustomerData(company, contact), [company, contact]);

  const asText = FIELDS
    .map(f => `${f.textLabel}: ${data[f.key]}`)
    .join('\n');
  const asJson = JSON.stringify(data, null, 2);

  function copy(what, value) {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(what);
      setTimeout(() => setCopied(c => (c === what ? '' : c)), 2000);
    }).catch(() => {});
  }

  const missing = FIELDS.filter(f => !data[f.key]).map(f => f.label);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Kopiera kunddata
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          För inklistring i offertsnurran (Flatbox)
        </Typography>
      </DialogTitle>
      <DialogContent>
        {contacts.length > 1 && (
          <TextField select fullWidth size="small" label="Kundkontakt"
            value={contactId} onChange={e => setContactId(e.target.value)}
            sx={{ mt: 1, mb: 2 }}>
            {contacts.map(c => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}{c.role ? ` – ${c.role}` : ''}{c.primary ? ' ★' : ''}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: contacts.length > 1 ? 0 : 1 }}>
          {FIELDS.map(f => (
            <Box key={f.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField fullWidth size="small" label={f.label}
                value={data[f.key]}
                InputProps={{ readOnly: true }}
                onFocus={e => e.target.select()} />
              <Tooltip title={`Kopiera ${f.label.toLowerCase()}`}>
                <span>
                  <IconButton size="small" disabled={!data[f.key]}
                    onClick={() => copy(f.key, data[f.key])}>
                    {copied === f.key
                      ? <CheckIcon fontSize="small" sx={{ color: '#2E7D32' }} />
                      : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>

        {missing.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Saknar värde: {missing.join(', ')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Stäng</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined"
          startIcon={copied === 'text' ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={() => copy('text', asText)}>
          {copied === 'text' ? 'Kopierat!' : 'Kopiera som text'}
        </Button>
        <Button variant="contained"
          startIcon={copied === 'json' ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={() => copy('json', asJson)}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#E35F00' } }}>
          {copied === 'json' ? 'Kopierat!' : 'Kopiera som JSON'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
